import ManIcon from "@mui/icons-material/Man";
import WomanIcon from "@mui/icons-material/Woman";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ActorAvatar from "../../components/actors/ActorAvatar";
import { browseListboxId, browseOptionId } from "../../components/browseListboxIds";
import PiBrowseDialog from "../../components/PiBrowseDialog";
import PiBrowseLayout from "../../components/PiBrowseLayout";
import PiLoadMoreSentinel from "../../components/PiLoadMoreSentinel";
import PiSegmentedToggle from "../../components/PiSegmentedToggle";
import useOutfitCatalog, { useOutfitSearch } from "../../hooks/OutfitCatalog.hook";
import useFilterableList from "../../hooks/useFilterableList";
import useGridColumns from "../../hooks/useGridColumns";
import { Actor, Gender } from "../../models/Actor";
import { Outfit } from "../../models/Outfit";
import {
  initialPreviewGender,
  itemValue,
  moveInBlocks,
  OutfitPickerContext,
  OutfitPickerItem,
  outfitItem,
  PreviewGender,
  specialItem,
} from "./outfitPickerModel";
import OutfitPickerDetails from "./OutfitPickerDetails";
import OutfitPickerTile, { OutfitPickerSkeletonTile } from "./OutfitPickerTile";

const DIALOG = "outfit-picker";
const TILE_WIDTH = 144;
const TILE_GAP = 16;
const INITIAL_SKELETON_ROWS = 3;

export interface OutfitPickerProps {
  context: OutfitPickerContext;
  /** The actor being dressed: names the dialog and sets the preview gender. */
  actor?: Actor;
  /** Current value; "" is Default (take) or Random (preset). */
  value: string;
  /** The actor's own outfit, which Default keeps (take settings only). */
  ownOutfit?: Outfit;
  onChoose: (value: string) => void;
  onClose: () => void;
}

export default function OutfitPickerDialog(props: OutfitPickerProps & { open: boolean }) {
  const { open, context, actor, onClose } = props;
  const { t } = useTranslation();
  const title = t(context === "setOutfit" ? "outfit_picker.title_set_outfit" : "outfit_picker.title", {
    name: actor?.workingName ?? "",
  });
  const scope = t(
    context === "take"
      ? "outfit_picker.scope_take"
      : context === "preset"
        ? "outfit_picker.scope_preset"
        : "outfit_picker.scope_set_outfit"
  );

  return (
    <PiBrowseDialog
      open={open}
      onClose={onClose}
      title={String(title)}
      icon={actor ? <ActorAvatar actor={actor} size="28" theme="creator" /> : undefined}
      scopeBadge={<span className="text-[14px] font-normal text-creator-text-sub">{scope}</span>}
      body={<OutfitPickerBody {...props} />}
    />
  );
}

function OutfitPickerBody(props: OutfitPickerProps) {
  const { context, actor, value, ownOutfit, onChoose, onClose } = props;
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [gender, setGender] = useState<PreviewGender>(initialPreviewGender(actor?.gender));

  const catalog = useOutfitCatalog();
  const search = useOutfitSearch(query);
  const source = query ? search : catalog;

  // The Default/Random tile leads the catalog while not searching.
  const items = useMemo(() => {
    const special = query ? undefined : specialItem(context);
    const outfits = source.outfits.map((outfit) => outfitItem(outfit, "all"));
    return special ? [special, ...outfits] : outfits;
  }, [query, context, source.outfits]);

  const resultsRef = useRef<HTMLDivElement | null>(null);
  const columns = useGridColumns(resultsRef, {
    minCellWidth: TILE_WIDTH,
    gap: TILE_GAP,
    fallback: 6,
    min: 2,
    max: 10,
  });

  const choose = useCallback(
    (item: OutfitPickerItem | undefined) => {
      if (!item) return;
      onChoose(itemValue(item));
      onClose();
    },
    [onChoose, onClose]
  );

  const list = useFilterableList<OutfitPickerItem>({
    items,
    // The query goes to the server; the list hook only drives the keyboard.
    getSearchStrings: () => [],
    columns,
    moveHighlight: (index, direction) => moveInBlocks(index, direction, 0, items.length, columns),
    initiallyOpen: true,
    focusSearchOnSlash: true,
    onSelect: (item) => {
      onChoose(itemValue(item));
    },
    onClose,
  });
  const { selectedIndex, setSelectedIndex, setOpen } = list;

  useEffect(() => {
    setOpen(true);
    return () => setOpen(false);
  }, [setOpen]);

  // Open on the current value: the special tile for "", else its tile.
  const initialised = useRef(false);
  useEffect(() => {
    if (initialised.current || items.length === 0) return;
    initialised.current = true;
    const index = items.findIndex((item) => itemValue(item) === value);
    setSelectedIndex(Math.max(index, 0));
  }, [items, value, setSelectedIndex]);

  const onSearchChange = useCallback(
    (next: string) => {
      setQuery(next.trim());
      setSelectedIndex(0);
    },
    [setSelectedIndex]
  );

  // Keep the keyboard highlight in view.
  useEffect(() => {
    document.getElementById(browseOptionId(DIALOG, selectedIndex))?.scrollIntoView?.({ block: "nearest" });
  }, [selectedIndex]);

  const highlighted = items[selectedIndex];
  const renderTile = (item: OutfitPickerItem, index: number) => (
    <OutfitPickerTile
      key={item.key}
      item={item}
      gender={gender}
      ownOutfit={ownOutfit}
      selected={itemValue(item) === value}
      highlighted={index === selectedIndex}
      optionId={browseOptionId(DIALOG, index)}
      onClick={() => setSelectedIndex(index)}
      onDoubleClick={() => choose(item)}
    />
  );

  const gridStyle = { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` };
  // Reserve a placeholder for every outfit not loaded yet, so the scroll height
  // is final from the first page on. Until the first page reports the total,
  // fill a few rows. The first placeholder carries the load-more sentinel: the
  // next page loads as the loaded tiles end, not at the far end of the list.
  const pending = source.hasMore
    ? source.totalCount === undefined
      ? Math.max(columns, 1) * INITIAL_SKELETON_ROWS
      : Math.max(source.totalCount - source.outfits.length, 0)
    : 0;
  const skeletons = Array.from({ length: pending }, (_, i) =>
    i === 0 ? (
      <div key="loading-0" className="relative">
        <OutfitPickerSkeletonTile />
        <div className="absolute inset-x-0 top-0">
          <PiLoadMoreSentinel onVisible={source.loadNextPage} />
        </div>
      </div>
    ) : (
      <OutfitPickerSkeletonTile key={`loading-${i}`} />
    )
  );
  const nothingFound = query !== "" && !source.hasMore && !source.isLoading && source.outfits.length === 0;

  const results = (
    <div
      ref={resultsRef}
      id={browseListboxId(DIALOG)}
      role="listbox"
      className="flex min-h-0 flex-1 flex-col gap-[24px] overflow-y-auto p-[24px] pit-quiet-scrollbar"
      data-testid="outfit-picker-results"
    >
      {nothingFound ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-[8px] text-center text-[14px] text-creator-text-sub">
          <div className="text-[16px] font-semibold text-creator-text">
            {t("outfit_picker.empty_title", { query })}
          </div>
          <div>{t("outfit_picker.empty_body")}</div>
        </div>
      ) : (
        <section className="flex flex-col gap-[12px]" data-testid="outfit-group-all">
          <div className="text-[14px] font-semibold text-creator-text">
            {t(query ? "outfit_picker.matches" : "outfit_picker.all_outfits")}
          </div>
          <div className="grid gap-x-[16px] gap-y-[24px]" style={gridStyle}>
            {items.map((item, index) => renderTile(item, index))}
            {skeletons}
          </div>
        </section>
      )}
    </div>
  );

  const primaryName =
    highlighted?.kind === "outfit"
      ? highlighted.outfit.name
      : highlighted?.kind === "default"
        ? t("outfit_picker.default")
        : highlighted?.kind === "random"
          ? t("outfit_picker.random")
          : undefined;

  return (
    <PiBrowseLayout
      searchValue={query}
      onSearchChange={onSearchChange}
      searchPlaceholder={t("outfit_picker.search_placeholder")}
      searchInputRef={list.searchInputRef}
      // The query goes to the server, so it waits longer than a client filter.
      searchDebounceMs={300}
      dataTestId="outfit-picker"
      listboxId={browseListboxId(DIALOG)}
      activeDescendantId={browseOptionId(DIALOG, selectedIndex)}
      scopeSwitcher={
        <div className="flex items-center gap-[8px] whitespace-nowrap text-[12px] text-creator-text-sub">
          {t("outfit_picker.preview_as")}
          <PiSegmentedToggle<PreviewGender>
            ariaLabel={String(t("outfit_picker.preview_as"))}
            value={gender}
            onChange={setGender}
            dense
            variant="square"
            options={[
              {
                value: Gender.male,
                testId: "outfit-preview-male",
                label: (
                  <>
                    <ManIcon fontSize="inherit" color="inherit" />
                    {t("outfit_picker.male")}
                  </>
                ),
              },
              {
                value: Gender.female,
                testId: "outfit-preview-female",
                label: (
                  <>
                    <WomanIcon fontSize="inherit" color="inherit" />
                    {t("outfit_picker.female")}
                  </>
                ),
              },
            ]}
          />
        </div>
      }
      // One portrait grid, so tiles without a view switch: the legend shows ← ↑ ↓ →.
      viewMode="tile"
      results={results}
      details={<OutfitPickerDetails item={highlighted} gender={gender} actor={actor} ownOutfit={ownOutfit} />}
      primaryLabel={
        primaryName ? String(t("outfit_picker.use", { name: primaryName })) : String(t("outfit_picker.use_outfit"))
      }
      primaryDisabled={!highlighted}
      onPrimaryAction={() => choose(highlighted)}
    />
  );
}

